import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';
import COMPANY_INFO from '../data/companyInfo';
import DISCLAIMER from '../data/invoiceDisclaimer';
import SignatureLines from './SignatureLines';
import ClientBlock from './ClientBlock';
import './InvoicePrintView.css';
import printWhenReady from '../utils/printWhenReady';
import { formatDay } from '../utils/dates';
import Loading from './Loading';
import { useFileName, documentName, invoiceDocType } from '../utils/fileName';
import { useAuth } from '../context/AuthContext';
import { canEditInvoice } from '../utils/invoiceEdit';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);

const TIPO_LABELS = {
  E31: 'Crédito Fiscal',
  E32: 'Consumidor Final',
  E33: 'Nota de Débito',
  E34: 'Nota de Crédito',
  E44: 'Régimen Especial',
  E45: 'Gubernamental',
  E46: 'Exportación',
  E47: 'Pagos al Exterior',
};

// What a factura not valid to hand out yet is, on the preview's banner.
const PREVIEW_STATUS = {
  draft: 'Borrador', ready: 'Lista: todavía no se ha enviado a DGII', pending_dgii: 'Enviada a DGII: esperando su respuesta',
  rejected: 'Rechazada por DGII', void: 'Anulada',
};

// `preview` (/invoices/:id/preview — where the email and WhatsApp notifications link): the factura on screen whatever its state,
// to check it before approving or sending it, with "Editar" when it can still be changed. One not valid yet (no NCF accepted by
// DGII) is marked VISTA PREVIA and can't be printed; an issued one prints as usual. The printout itself (/print) stays blocked
// until DGII accepts it.
const InvoicePrintView = ({ preview = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isAdmin = user?.role === 'admin';
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState('');
  const [protocolOn, setProtocolOn] = useState(false);

  useEffect(() => {
    API.get(`/invoices/${id}`).then(res => setInvoice(res.data)).catch(err => {
      console.error('Error loading invoice:', err);
      setError('No se pudo cargar la factura.');
    });
  }, [id]);
  useEffect(() => { if (preview) API.get('/invoices/internal-e32').then(res => setProtocolOn(!!res.data.enabled)).catch(() => {}); }, [preview]);

  const notYetIssued = invoice && (invoice.status === 'draft' || invoice.status === 'ready' || invoice.status === 'pending_dgii');
  const rejected = invoice && invoice.status === 'rejected';
  const blocked = notYetIssued || rejected;
  const notValid = blocked || invoice?.status === 'void';

  useEffect(() => {
    if (invoice && !blocked && !preview) {
      let cancelPrint = () => {};
      const t = setTimeout(() => { cancelPrint = printWhenReady(); }, 350);
      return () => { clearTimeout(t); cancelPrint(); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  // Saved as PDF: <Cliente>Factura / NotaDeCredito / NotaDeDebito (utils/fileName.js).
  useFileName(invoice && documentName(invoice.client?.name, invoiceDocType(invoice.tipoEcf)));

  if (error) return <div className="p-4 text-danger">{error}</div>;
  if (!invoice) return <Loading page label="Cargando factura..." />;

  if (rejected && !preview) {
    return (
      <div className="p-4">
        <div className="alert alert-danger mb-0">
          Esta factura fue <strong>rechazada por DGII</strong> — no tiene un NCF válido y no se puede imprimir ni entregar
          al cliente. El e-NCF {invoice.ncf || ''} ya fue consumido por DGII y no puede reutilizarse; crea una nueva
          factura para esta venta.
          {invoice.ecf?.dgiiMessage && (
            <div className="mt-2"><strong>Motivo:</strong> {invoice.ecf.dgiiMessage}</div>
          )}
        </div>
      </div>
    );
  }

  if (invoice.status === 'pending_dgii' && !preview) {
    return (
      <div className="p-4">
        <div className="alert alert-warning mb-0">
          Esta factura fue enviada a DGII y está esperando confirmación (normalmente toma solo unos segundos) — no se
          puede imprimir hasta que DGII confirme su aceptación. Vuelve a intentar en un momento.
        </div>
      </div>
    );
  }

  if (notYetIssued && !preview) {
    return (
      <div className="p-4">
        <div className="alert alert-warning mb-0">
          Esta factura todavía no ha sido enviada a DGII y aceptada (sin NCF válido) — no se puede imprimir ni entregar
          al cliente hasta ese momento. Ve a Facturas y usa "Enviar a DGII" primero.
        </div>
      </div>
    );
  }

  const client = invoice.client || {};
  // Credit notes are stored negative; printed notes always show positive amounts.
  const isNote = ['E33', 'E34'].includes(invoice.tipoEcf);
  const shown = isNote ? Math.abs : (n) => n;
  // A code-2 credit note only corrects wording: no quantities or amounts to show.
  const textOnly = invoice.tipoEcf === 'E34' && invoice.modificationCode === 2;
  const documentTitle = { E33: 'NOTA DE DÉBITO', E34: 'NOTA DE CRÉDITO' }[invoice.tipoEcf] || 'FACTURA';

  return (
    <div className="invoice-page-wrapper">
      {preview && notValid && (
        <div className="no-print alert alert-warning mx-auto mb-3" style={{ maxWidth: '8.5in' }}>
          <strong>Vista previa — {PREVIEW_STATUS[invoice.status]}.</strong>{' '}
          {invoice.status === 'void' ? 'No tiene validez.' : 'Todavía no es válida para entregar al cliente: se puede imprimir cuando DGII la acepte.'}
          {rejected && invoice.ecf?.dgiiMessage && <div className="mt-1"><strong>Motivo:</strong> {invoice.ecf.dgiiMessage}</div>}
        </div>
      )}
      <div className="no-print text-center mb-3 d-flex gap-2 justify-content-center">
        {preview && canEditInvoice(invoice, { isAdmin, protocolOn }) && (
          <button className="btn btn-outline-primary" onClick={() => navigate(`/invoices/${invoice._id}/edit`)}>
            <i className="bi bi-pencil" /> Editar
          </button>
        )}
        {!notValid && (
          <button className="btn btn-primary" onClick={() => window.print()}>
            <i className="bi bi-printer" /> Imprimir / Guardar como PDF
          </button>
        )}
        {preview && (
          <button className="btn btn-outline-secondary" onClick={() => navigate('/invoices')}>
            <i className="bi bi-list-ul" /> Ir a Facturas
          </button>
        )}
      </div>

      <div className={`invoice-paper${preview && notValid ? ' invoice-preview-only' : ''}`}>
        {preview && notValid && <div className="invoice-preview-watermark" aria-hidden="true">VISTA PREVIA</div>}
        {/* .invoice-body flows normally; .invoice-bottom (signatures + thank-you note) is pinned to the bottom of the page in print. */}
        <div className="invoice-body">
        <div className="invoice-header-row">
          <div className="invoice-company-block">
            <img src="/logo-print.png" alt="Intmed" className="invoice-logo" />
            <div style={{ fontWeight: 700, color: '#0B1B29' }}>{COMPANY_INFO.name}</div>
            <div>RNC: {COMPANY_INFO.rnc}</div>
            <div>{COMPANY_INFO.address}</div>
            <div>{COMPANY_INFO.phone} · {COMPANY_INFO.email}</div>
            <div>{COMPANY_INFO.website}</div>
          </div>
          <div className="invoice-meta-block">
            <div className="invoice-title">{documentTitle}</div>
            <div>No.: {invoice.number}</div>
            {/* Protocolo 514 (an internal factura): no NCF and no "Tipo: E32" — just its kind, in bold. Once it is
                reported through Ventas (sent to DGII and accepted) it prints like any other. */}
            {invoice.internal && !invoice.ncf ? (
              <div style={{ fontWeight: 700 }}>{TIPO_LABELS[invoice.tipoEcf] || ''}</div>
            ) : (
              <>
                <div>NCF: {invoice.ncf || 'Pendiente'}</div>
                <div>Tipo: {invoice.tipoEcf} — {TIPO_LABELS[invoice.tipoEcf] || ''}</div>
              </>
            )}
            <div>Fecha: {dateStr(invoice.ecf?.emissionDate || invoice.createdAt)}</div>
            {isNote ? (
              <>
                <div>Modifica: {invoice.modifiedNcf} ({dateStr(invoice.modifiedDate)})</div>
                {invoice.modificationReason && <div>Motivo: {invoice.modificationReason}</div>}
              </>
            ) : (
              <div>Vencimiento: {dateStr(invoice.dueDate)}</div>
            )}
          </div>
        </div>

        <ClientBlock label="Facturar a" client={client} address={client.address} />

        <table className="invoice-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Descripción</th>
              <th className="text-center">Cant.</th>
              <th className="text-end">Precio Unit.</th>
              <th className="text-center">ITBIS</th>
              <th className="text-end">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, i) => {
              const lineSubtotal = shown(item.quantity * item.unitPrice);
              const lineItbis = item.itbisExento ? 0 : lineSubtotal * (item.itbisRate ?? 0.18);
              return (
                <tr key={i}>
                  <td>{item.sku || '—'}</td>
                  <td>{item.description}</td>
                  <td className="text-center">{textOnly ? '—' : shown(item.quantity)}</td>
                  <td className="text-end">{textOnly ? '—' : money(item.unitPrice)}</td>
                  <td className="text-center">{money(lineItbis)}</td>
                  <td className="text-end">{money(lineSubtotal + lineItbis)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="invoice-totals">
          <div className="invoice-totals-box">
            <div className="invoice-totals-row">
              <span>Subtotal</span>
              <span>{money(shown(invoice.subtotal))}</span>
            </div>
            <div className="invoice-totals-row">
              <span>ITBIS</span>
              <span>{money(shown(invoice.itbisTotal))}</span>
            </div>
            <div className="invoice-totals-row grand">
              <span>Total</span>
              <span>{money(shown(invoice.total))}</span>
            </div>
          </div>
        </div>

        {invoice.notes && (
          <div className="invoice-bill-to">
            <div className="label">Notas</div>
            <div style={{ whiteSpace: 'pre-line' }}>{invoice.notes}</div>
          </div>
        )}

        {!isNote && <div className="invoice-disclaimer">{DISCLAIMER}</div>}

        </div>
        <div className="invoice-bottom">
        {/* Every document is signed and sealed — notes too (the disclaimer above is for facturas only). */}
        <SignatureLines clientName={client.name} />

        <div className="invoice-footer-note">Gracias por su preferencia — {COMPANY_INFO.name}</div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintView;
